import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  CheckCircle,
  ChevronLeft,
  RotateCcw,
  Maximize,
  Minimize,
  Loader2,
} from "lucide-react";
import PlaybackTimeline from "./PlaybackTimeline";

const VideoPlayerStep = ({
  onBack,
  playbackError,
  onRetry,
  videoRef,
  isPlaying: externalIsPlaying,
  isMuted: externalIsMuted,
  onPlayPause: externalOnPlayPause,
  onMuteToggle: externalOnMuteToggle,
  onFullscreen: externalOnFullscreen,
  onStartTimeChange,
  onEndTimeChange,
  onTimeRangeChange,
  isHLSLoading = false,
  hlsRetryAttempt = 0,
}) => {
  const [isPlaying, setIsPlaying] = useState(externalIsPlaying || false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(externalIsMuted || false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [controlsTimeout, setControlsTimeout] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasStartedLoading, setHasStartedLoading] = useState(false);
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState(new Date());
  const containerRef = useRef(null);

  const handlePlayPause = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (externalOnPlayPause) {
      externalOnPlayPause(!isPlaying);
    } else if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(err => console.error('Play error:', err));
      }
    }
  }, [externalOnPlayPause, isPlaying, videoRef]);

  const handleTimelineTimeChange = useCallback((time) => {
    setCurrentPlaybackTime(time);
    // You can add logic here to seek the video to the selected time
    console.log('Timeline time changed to:', time);
  }, []);


  const handleVolumeChange = useCallback((e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
  }, [videoRef]);

  const handleMuteToggle = useCallback(() => {
    if (externalOnMuteToggle) {
      externalOnMuteToggle();
    } else {
      setIsMuted(!isMuted);
      if (videoRef.current) {
        videoRef.current.muted = !isMuted;
      }
    }
  }, [externalOnMuteToggle, isMuted, videoRef]);

  const handleSeek = useCallback((e) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
    }
  }, [videoRef]);

  const handlePlaybackRateChange = useCallback((rate) => {
    setPlaybackRate(rate);
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
    }
  }, [videoRef]);

  const handleFullscreen = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (externalOnFullscreen) {
      externalOnFullscreen();
    } else if (containerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(err => console.error('Exit fullscreen error:', err));
      } else {
        const container = containerRef.current;
        if (container.requestFullscreen) {
          container.requestFullscreen().catch(err => console.error('Fullscreen error:', err));
        } else if (container.webkitRequestFullscreen) {
          container.webkitRequestFullscreen();
        } else if (container.mozRequestFullScreen) {
          container.mozRequestFullScreen();
        } else if (container.msRequestFullscreen) {
          container.msRequestFullscreen();
        }
      }
    }
  }, [externalOnFullscreen]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Auto-hide controls
  const resetControlsTimeout = useCallback(() => {
    if (controlsTimeout) {
      clearTimeout(controlsTimeout);
    }
    setShowControls(true);
    const timeout = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
    setControlsTimeout(timeout);
  }, [controlsTimeout, isPlaying]);

  const handleMouseMove = useCallback(() => {
    resetControlsTimeout();
  }, [resetControlsTimeout]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => setCurrentTime(video.currentTime);
    const handleDurationChange = () => setDuration(video.duration);
    const handlePlay = () => {
      setIsPlaying(true);
    };
    const handlePause = () => {
      setIsPlaying(false);
    };
    const handleVolumeUpdate = () => {
      setVolume(video.volume);
      setIsMuted(video.muted);
    };
    const handleLoadStart = () => {
      setIsLoading(true);
      setHasStartedLoading(true);
    };
    const handleCanPlay = () => {
      setIsLoading(false);
    };
    const handleCanPlayThrough = () => {
      setIsLoading(false);
    };
    const handleWaiting = () => {
      setIsLoading(true);
    };
    const handlePlaying = () => {
      setIsLoading(false);
    };
    const handleError = () => {
      setIsLoading(false);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('volumechange', handleVolumeUpdate);
    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('canplaythrough', handleCanPlayThrough);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('error', handleError);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('volumechange', handleVolumeUpdate);
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('canplaythrough', handleCanPlayThrough);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('error', handleError);
    };
  }, [videoRef]);

  // Keyboard shortcuts effect
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!videoRef.current) return;
      
      const video = videoRef.current;
      
      switch (e.key) {
        case ' ':
          e.preventDefault();
          handlePlayPause();
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          handleFullscreen();
          break;
        case 'm':
        case 'M':
          e.preventDefault();
          handleMuteToggle();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          video.currentTime = Math.max(0, video.currentTime - 5);
          break;
        case 'ArrowRight':
          e.preventDefault();
          video.currentTime = Math.min(video.duration, video.currentTime + 5);
          break;
        case 'ArrowUp':
          e.preventDefault();
          video.volume = Math.min(1, video.volume + 0.1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          video.volume = Math.max(0, video.volume - 0.1);
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handlePlayPause, handleFullscreen, handleMuteToggle, videoRef]);

  // Control timeout effect
  useEffect(() => {
    if (isPlaying) {
      resetControlsTimeout();
    } else {
      setShowControls(true);
      if (controlsTimeout) {
        clearTimeout(controlsTimeout);
      }
    }
  }, [isPlaying]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (controlsTimeout) {
        clearTimeout(controlsTimeout);
      }
    };
  }, [controlsTimeout]);

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 bg-black z-50 flex flex-col justify-center' : 'space-y-6'}`} ref={containerRef}>
      {/* Success Message */}
      {!isFullscreen && (
        <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900">
              Video Ready!
            </h2>
          </div>
          <button
            onClick={onBack}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Back</span>
          </button>
        </div>

        <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span className="text-green-700 font-medium">
              Your video is ready to watch!
            </span>
          </div>
        </div>
        </div>
      )}

      {/* Playback Timeline */}
      {!isFullscreen && (
        <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
          <PlaybackTimeline
            currentTime={currentPlaybackTime}
            onTimeChange={handleTimelineTimeChange}
            onStartTimeChange={onStartTimeChange}
            onEndTimeChange={onEndTimeChange}
            onTimeRangeChange={onTimeRangeChange}
          />
        </div>
      )}

      {/* Enhanced Video Player */}
      <div className={`${isFullscreen ? 'bg-black h-full flex flex-col' : 'bg-white rounded-2xl shadow-xl p-6 border border-gray-100'}`}>
        {!isFullscreen && (
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-900">
              Video Player
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={onRetry}
                className="flex items-center space-x-2 px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors text-sm"
              >
                <RotateCcw className="h-4 w-4" />
                <span>Retry</span>
              </button>
            </div>
          </div>
        )}

        {playbackError && !isFullscreen && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center space-x-2">
              <div className="h-5 w-5 text-red-500">⚠️</div>
              <span className="text-red-700 text-sm font-medium">
                {playbackError}
              </span>
            </div>
          </div>
        )}

        {/* Video Player */}
        <div 
          className={`relative bg-black overflow-hidden ${isFullscreen ? 'flex-1' : 'rounded-xl'}`}
          style={{ minHeight: isFullscreen ? '100%' : '400px' }}
          onMouseMove={handleMouseMove}
        >
          <video
            ref={videoRef}
            className={`w-full h-full ${isFullscreen ? '' : 'rounded-xl'}`}
            style={{ height: isFullscreen ? '100%' : '400px' }}
            preload="auto"
            playsInline
            controls={false}
            onClick={() => {
              setShowControls(true);
              resetControlsTimeout();
            }}
            onLoadStart={() => console.log('🎯 Video: Load started')}
            onLoadedMetadata={() => console.log('🎯 Video: Metadata loaded')}
            onCanPlay={() => console.log('🎯 Video: Can play')}
            onError={(e) => console.error('🎯 Video error:', e)}
          >
            <p className="text-white p-4">
              Your browser does not support the video tag.
            </p>
          </video>

          {/* Loading Overlay */}
          {(isLoading && hasStartedLoading) || isHLSLoading ? (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
              <Loader2 className="w-12 h-12 text-white animate-spin" />
            </div>
          ) : null}

          {/* Custom Controls Overlay */}
          <div className={`absolute inset-0 transition-opacity duration-300 ${
            showControls ? 'opacity-100' : 'opacity-0'
          }`} style={{ pointerEvents: 'none' }}>
            {/* Top Controls */}
            <div className="absolute top-4 right-4 flex items-center space-x-2" style={{ pointerEvents: 'auto', zIndex: 20 }}>
              {/* Playback Rate */}
              <select
                value={playbackRate}
                onChange={(e) => handlePlaybackRateChange(parseFloat(e.target.value))}
                className="px-3 py-1.5 bg-black/70 text-white rounded-lg text-sm border border-white/20 backdrop-blur-sm"
                style={{ pointerEvents: 'auto', zIndex: 21 }}
              >
                <option value={0.25}>0.25x</option>
                <option value={0.5}>0.5x</option>
                <option value={0.75}>0.75x</option>
                <option value={1}>1x</option>
                <option value={1.25}>1.25x</option>
                <option value={1.5}>1.5x</option>
                <option value={2}>2x</option>
              </select>

              {/* Fullscreen Button */}
              <button
                onClick={handleFullscreen}
                className="p-2.5 bg-black/70 hover:bg-black/90 text-white rounded-lg transition-colors backdrop-blur-sm"
                title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                style={{ pointerEvents: 'auto', zIndex: 21 }}
              >
                {isFullscreen ? (
                  <Minimize className="w-5 h-5" />
                ) : (
                  <Maximize className="w-5 h-5" />
                )}
              </button>
            </div>

            {/* Center Play Button */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-auto">
              <button
                onClick={handlePlayPause}
                className="p-4 bg-black/70 hover:bg-black/90 text-white rounded-full transition-all duration-300 hover:scale-110 backdrop-blur-sm shadow-lg"
                title="Play/Pause"
              >
                {isPlaying ? (
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                  </svg>
                ) : (
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="m7 4 10 6-10 6V4z"/>
                  </svg>
                )}
              </button>
            </div>

            {/* Bottom Controls */}
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent pointer-events-auto">
              {/* Progress Bar */}
              <div className="w-full mb-3">
                <input
                  type="range"
                  min="0"
                  max={duration || 0}
                  value={currentTime}
                  onChange={handleSeek}
                  className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${duration ? (currentTime / duration) * 100 : 0}%, rgba(255,255,255,0.2) ${duration ? (currentTime / duration) * 100 : 0}%, rgba(255,255,255,0.2) 100%)`
                  }}
                />
              </div>

              {/* Control Buttons Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {/* Play/Pause */}
                  <button
                    onClick={handlePlayPause}
                    className="p-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
                    title="Play/Pause"
                  >
                    {isPlaying ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="m7 4 10 6-10 6V4z"/>
                      </svg>
                    )}
                  </button>

                  {/* Volume Control */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleMuteToggle}
                      className="p-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
                      title="Mute/Unmute"
                    >
                      {isMuted || volume === 0 ? (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.793L4.5 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.5l3.883-3.793a1 1 0 011.414.076zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.793L4.5 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.5l3.883-3.793a1 1 0 011.414.076zM14 5.24a.5.5 0 01.806.396V14.36a.5.5 0 01-.806.396A6.967 6.967 0 0112 10a6.967 6.967 0 012-4.76z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={isMuted ? 0 : volume}
                      onChange={handleVolumeChange}
                      className="w-20 h-1 bg-white/20 rounded-lg appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.2) ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.2) 100%)`
                      }}
                    />
                  </div>
                </div>

                {/* Time Display */}
                <div className="text-white text-sm font-medium bg-black/50 px-3 py-1.5 rounded backdrop-blur-sm">
                  {formatTime(currentTime)} / {formatTime(duration || 0)}
                </div>
              </div>
            </div>
          </div>

        </div>

        {!isFullscreen && (
          <>
            {/* Player Features Info */}
            <div className="mt-4 text-xs text-gray-500 text-center">
              <span className="font-medium">Video Player Features:</span> Full controls • Keyboard shortcuts • Multiple playback speeds • Fullscreen • Volume control
            </div>
            
            {/* Keyboard Shortcuts Help */}
            <div className="mt-2 text-xs text-gray-500 text-center">
              <span className="font-medium">Keyboard shortcuts:</span> Space (play/pause) • ←/→ (seek ±5s) • ↑/↓ (volume ±10%) • M (mute) • F (fullscreen)
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VideoPlayerStep;
